import { useCallback, useSyncExternalStore } from "react";

// The board lists the user has collapsed, by list id, so a list collapsed
// out of the way (a long "Someday" list, say) stays collapsed after a
// refresh. Kept per browser, the same way the iOS and Android apps keep it
// per device — it is how this screen looks here, not account data.
//
// This is deliberately not part of the saved view (useViewStatePersistence):
// that snapshot is restored once per tab, and only on a bare "/" entry,
// while a collapse has to hold on every board, whatever the URL.
export const COLLAPSED_LISTS_STORAGE_KEY = "judi-collapsed-lists";

const EMPTY: ReadonlySet<string> = new Set();

// The collapsed ids live in memory, so a render never touches storage.
// localStorage is read again only while no board is subscribed — a board
// that mounts picks up whatever was saved since the last one left — and
// when another tab writes the key. A click updates memory and writes
// through. The raw string is kept so an unchanged value keeps the same Set
// instance, which useSyncExternalStore needs as its stable snapshot.
let cachedRaw: string | null = null;
let collapsedIds: ReadonlySet<string> = EMPTY;
// Set once a write has failed (private browsing, quota exceeded): memory
// then holds a collapse that storage doesn't, so storage must not overwrite
// it. The collapse still works for the rest of this tab, it just isn't
// remembered — the same graceful degradation the saved view state has.
let unsaved = false;

const listeners = new Set<() => void>();

function readRaw(): string | null {
  try {
    return localStorage.getItem(COLLAPSED_LISTS_STORAGE_KEY);
  } catch {
    return null;
  }
}

function parse(raw: string | null): ReadonlySet<string> {
  if (!raw) return EMPTY;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return EMPTY;
    return new Set(parsed.filter((id): id is string => typeof id === "string"));
  } catch {
    return EMPTY;
  }
}

function syncFromStorage(): void {
  const raw = readRaw();
  if (raw === cachedRaw) return;
  cachedRaw = raw;
  collapsedIds = parse(raw);
}

function getSnapshot(): ReadonlySet<string> {
  if (listeners.size === 0 && !unsaved) syncFromStorage();
  return collapsedIds;
}

function subscribe(onChange: () => void): () => void {
  listeners.add(onChange);
  // Another tab collapsing a list writes the same key; follow it here too.
  // A tab that did save is newer than anything this tab kept in memory.
  const onStorage = (event: StorageEvent) => {
    if (event.key !== COLLAPSED_LISTS_STORAGE_KEY && event.key !== null) return;
    unsaved = false;
    syncFromStorage();
    onChange();
  };
  window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(onChange);
    window.removeEventListener("storage", onStorage);
  };
}

function toggle(listId: string, knownListIds: ReadonlySet<string>): void {
  const current = getSnapshot();
  // Ids of lists that are gone — deleted, or a shared list the user left —
  // are dropped on the way through, so the saved value can't only grow.
  const next = new Set([...current].filter((id) => knownListIds.has(id)));
  if (current.has(listId)) next.delete(listId);
  else next.add(listId);
  collapsedIds = next;
  const raw = JSON.stringify([...next]);
  try {
    localStorage.setItem(COLLAPSED_LISTS_STORAGE_KEY, raw);
    cachedRaw = raw;
    // Saving works again, so what was saved is the truth once more.
    unsaved = false;
  } catch {
    unsaved = true;
  }
  listeners.forEach((listener) => listener());
}

interface UseCollapsedListsArgs {
  isMobile: boolean;
  isSearching: boolean;
  // Every list the user has, so a toggle can drop the ids of lists that
  // no longer exist.
  lists: ReadonlyArray<{ id: string }>;
}

export function useCollapsedLists({
  isMobile,
  isSearching,
  lists,
}: UseCollapsedListsArgs) {
  const collapsedListIds = useSyncExternalStore(subscribe, getSnapshot);
  // Only the side-by-side layout collapses a list. The phone layout swipes
  // between full-width lists, where a collapsed one would be a blank page —
  // the iOS and Android apps offer no collapse in their swipe mode either —
  // so there every list shows its tasks, whatever was collapsed here.
  // A search opens every list too: a match inside a collapsed list would be
  // a header with a count and no card, and the search would look empty. The
  // saved collapse is untouched, so clearing the search closes them again.
  const canCollapse = !isMobile && !isSearching;
  const isCollapsed = useCallback(
    (listId: string | undefined) =>
      canCollapse && !!listId && collapsedListIds.has(listId),
    [canCollapse, collapsedListIds],
  );
  const toggleListCollapsed = useCallback(
    (listId: string) => toggle(listId, new Set(lists.map((list) => list.id))),
    [lists],
  );
  return { canCollapse, isCollapsed, toggleListCollapsed };
}
